// The browser's civil clock.
//
// Product dates still come from the API: the day, month and year screens ask
// the server for `today`, `this` and their neighbours. The daily quote is the
// one deliberately local exception. It is a bundled piece of presentation,
// selected for the browser's calendar date, so it must turn over even when no
// request or reload gives React a reason to render.

const DAY_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

export function localTimeZone(): string | null {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return typeof timeZone === 'string' && timeZone.length > 0 ? timeZone : null;
}

function dayFormatter(timeZone: string): Intl.DateTimeFormat {
  const known = DAY_FORMATTERS.get(timeZone);
  if (known !== undefined) return known;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  DAY_FORMATTERS.set(timeZone, formatter);
  return formatter;
}

/** The calendar date at an instant in an IANA zone, always `YYYY-MM-DD`. */
export function civilDayIn(timeZone: string, now: Date = new Date()): string {
  const parts = dayFormatter(timeZone).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((value) => value.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * Time until the zone changes date, rather than "24 hours from now".
 *
 * Local days at daylight-saving boundaries are 23 or 25 hours. Start beyond
 * either, then binary-search the first instant whose formatted date differs.
 * This mirrors the API's `millisecondsUntilCivilDayChanges`; the web cannot
 * import server code, and the boundary linter keeps that separation honest.
 */
export function millisecondsUntilCivilDayChanges(timeZone: string, now: Date): number {
  const start = now.getTime();
  const day = civilDayIn(timeZone, now);
  let sameDay = start;
  let differentDay = start + 36 * 60 * 60 * 1_000;

  while (civilDayIn(timeZone, new Date(differentDay)) === day) {
    differentDay += 24 * 60 * 60 * 1_000;
  }

  while (differentDay - sameDay > 1) {
    const middle = sameDay + Math.floor((differentDay - sameDay) / 2);
    if (civilDayIn(timeZone, new Date(middle)) === day) sameDay = middle;
    else differentDay = middle;
  }

  return differentDay - start;
}
